import { useRouter } from 'expo-router'
import type { PropsWithChildren, ReactNode } from 'react'
import { Platform, View } from 'react-native'

import { Header } from '@/src/elements/header/Header'
import { UserProfilePreview } from '@/src/elements/user-profile/UserProfilePreview/UserProfilePreview'
import { UserProfileWithModal } from '@/src/elements/user-profile/UserProfilePreviewWithModal'
import type { TConversationApi } from '@/src/features/message/api/types/messageApi.types'
import type { TMediumUserApi } from '@/src/features/user/api/userApi.types'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useBackHandlePress } from '@/src/general/hooks/useBackHandlePress'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { useRoutes } from '@/src/general/utils/routes'

import { ConversationOptions } from '../../../components/ConversationOptions'
import { ConversationGradientBackground } from './ConversationGradientBackground'
import { ViewPortSizeView } from './ViewPortSizeView'

type TDesktopConversationHeaderLayoutProps = {
  conversation?: TConversationApi
  openedConversationId: number | undefined
  user?: TMediumUserApi
}

const Container = Platform.OS === 'web' ? ViewPortSizeView : View
export const ConversationHeaderLayout = ({
  children,
  conversation,
  openedConversationId,
  user,
}: PropsWithChildren<TDesktopConversationHeaderLayoutProps>): ReactNode => {
  const { xs } = useDimensions()
  const { routes } = useRoutes()
  const router = useRouter()
  const { handleBackPress } = useBackHandlePress(routes.messages())

  const onClickReturn = () => {
    if (router.canDismiss()) router.dismiss()
    else handleBackPress()
  }

  if (xs)
    return (
      <Container className="h-full w-full pt-4 native:pb-safe-insets-b">
        <ConversationGradientBackground />
        <Header
          userInfos={user}
          leftButtons={[
            {
              startIcon: ICON.ARROW_LEFT,
              onPress: onClickReturn,
              testID: TEST_ID.CONVERSATION.HEADER.BACK,
              type: 'icon',
            },
          ]}
          rightButtons={
            conversation
              ? [
                  {
                    type: 'button',
                    component: () => (
                      <ConversationOptions
                        conversation={conversation}
                        openedConversationId={openedConversationId}
                        testID={TEST_ID.CONVERSATION.HEADER.OPTIONS_BUTTON}
                      />
                    ),
                  },
                ]
              : []
          }
        />
        <View className="flex native:h-full flex-1 flex-col items-stretch justify-center">{children}</View>
      </Container>
    )

  return (
    <>
      <ConversationGradientBackground />
      <View className="native:flex-1 flex w-full justify-between px-4 py-1 web:flex-row web:md:gap-2">
        {user && (
          <UserProfileWithModal user={user}>
            <UserProfilePreview.Avatar user={user} />
          </UserProfileWithModal>
        )}
        {conversation && (
          <ConversationOptions conversation={conversation} openedConversationId={openedConversationId} />
        )}
      </View>
      {children}
    </>
  )
}
